// contexts/AuthContext.tsx
import { User} from '@bookstore/shared';
import { getFirebaseAuth } from '@bookstore/shared/browser';
import { userService } from '@/services/user.service';
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  const auth = getFirebaseAuth();

  /**
   * Fetch user profile from Firestore
   * Creates profile if it doesn't exist
   */
  const fetchOrCreateUserProfile = async (
    firebaseUser: FirebaseUser
  ): Promise<User | null> => {
    try {
      // Try to get existing profile
      let profile = await userService.getUserProfile(firebaseUser.uid);

      // Create profile if it doesn't exist
      if (!profile) {
        
        await userService.createUserProfile(firebaseUser.uid, {
          name: firebaseUser.displayName || 'User',
          email: firebaseUser.email || '',
          phone: firebaseUser.phoneNumber || undefined,
          photoURL: firebaseUser.photoURL || undefined,
        });

        // Fetch the newly created profile
        profile = await userService.getUserProfile(firebaseUser.uid);
      }

      return profile;
    } catch (error) {
      console.error('Error fetching/creating user profile:', error);
      return null;
    }
  };

  /**
   * Refresh user data from Firestore
   */
  const refreshUser = async () => {
    if (!firebaseUser) return;

    try {
      const profile = await userService.getUserProfile(firebaseUser.uid);
      setUser(profile);
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  /**
   * Listen to Firebase Auth state changes
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setFirebaseUser(firebaseUser);

      if (firebaseUser) {
        // Fetch or create user profile
        const userProfile = await fetchOrCreateUserProfile(firebaseUser);
        setUser(userProfile);
      } else {
        setUser(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  /**
   * Sign out user
   */
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setFirebaseUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    firebaseUser,
    loading,
    signOut,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};